import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Spinner,
} from '@fluentui/react-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface MemoryDeleteConfirmDialogProps {
    open: boolean;
    // 'one' confirms deleting a single note, 'all' confirms clearing every note, 'optOut' confirms
    // turning memory off (which also purges every stored note server-side). Drives the copy.
    mode: 'one' | 'all' | 'optOut';
    // True while the request is in flight; keeps the buttons disabled so a double-click can't fire twice.
    busy: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Confirms a destructive memory action before it is sent — deleting a note, clearing all notes, or
 * turning memory off (which purges every note). Memory is user data, so its removal is always an
 * explicit, confirmed action, including the opt-out toggle. Mirrors ExportConfirmDialog.
 */
export const MemoryDeleteConfirmDialog: FC<MemoryDeleteConfirmDialogProps> = ({
    open,
    mode,
    busy,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation();
    const titleKey =
        mode === 'optOut'
            ? 'settings.memory.optOutConfirmTitle'
            : mode === 'all'
              ? 'settings.memory.deleteAllConfirmTitle'
              : 'settings.memory.deleteOneConfirmTitle';
    const bodyKey =
        mode === 'optOut'
            ? 'settings.memory.optOutConfirmBody'
            : mode === 'all'
              ? 'settings.memory.deleteAllConfirmBody'
              : 'settings.memory.deleteOneConfirmBody';
    const confirmKey = mode === 'optOut' ? 'settings.memory.optOutConfirm' : 'settings.memory.confirmDelete';

    return (
        <Dialog
            open={open}
            onOpenChange={(_, data) => {
                // Dismiss (Esc / backdrop) counts as cancel; never mid-delete so we don't strand a
                // pending request.
                if (!data.open && !busy) {
                    onCancel();
                }
            }}
        >
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{t(titleKey)}</DialogTitle>
                    <DialogContent>{t(bodyKey)}</DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" disabled={busy} onClick={onCancel}>
                            {t('settings.memory.cancel')}
                        </Button>
                        <Button
                            appearance="primary"
                            disabled={busy}
                            icon={busy ? <Spinner size="tiny" /> : undefined}
                            onClick={onConfirm}
                            data-testid="memory-delete-confirm"
                        >
                            {t(confirmKey)}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
